import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { clearCart } from '../redux/cartSlice';
import { apiFetch } from '../utils/api';
import { 
  FREE_DELIVERY_THRESHOLD, 
  DELIVERY_CHARGE, 
  PACKING_CHARGE_STANDARD, 
  PACKING_CHARGE_FREE_DELIVERY 
} from '../utils/cartConfig';
import '../styles/checkout.css';

const Payment = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);

  // Redux state selectors
  const cartItems = useSelector((state) => state.cart.cartItems);
  const appliedCoupon = useSelector((state) => state.cart.appliedCoupon);

  // Get selected address from state or sessionStorage
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [paymentOption, setPaymentOption] = useState('advance'); // 'advance' or 'full_prepaid'
  const [loading, setLoading] = useState(false);

  // 1. Route Protection & Load Address
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (cartItems.length === 0) {
      navigate('/cart');
      return;
    }

    // Try loading address from location state first, then sessionStorage
    let address = null;
    if (location.state && location.state.selectedAddress) {
      address = location.state.selectedAddress;
    } else {
      const stored = sessionStorage.getItem('selectedAddress');
      if (stored) {
        try {
          address = JSON.parse(stored);
        } catch (e) {
          console.error('Failed to parse selected address:', e);
        }
      }
    }

    if (!address) {
      alert("Please select a delivery address in Step 1 first.");
      navigate('/checkout');
      return;
    }

    setSelectedAddress(address);
  }, [user, cartItems.length, navigate, location.state]);

  // 2. Calculations (matching Step 1/Cart)
  const subtotal = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + (item.price * item.qty), 0);
  }, [cartItems]);

  const discountAmount = useMemo(() => {
    if (!appliedCoupon) return 0;
    if (appliedCoupon.discountType === 'FixedAmount') {
      return Math.min(subtotal, Number(appliedCoupon.discountValue) || 0);
    }
    return Math.round((subtotal * (Number(appliedCoupon.discountValue) || 0)) / 100);
  }, [subtotal, appliedCoupon]);

  const deliveryCharge = useMemo(() => {
    if (cartItems.length === 0) return 0;
    return subtotal >= FREE_DELIVERY_THRESHOLD ? 0 : DELIVERY_CHARGE;
  }, [cartItems.length, subtotal]);

  const packingCharge = useMemo(() => {
    if (cartItems.length === 0) return 0;
    return subtotal >= FREE_DELIVERY_THRESHOLD ? PACKING_CHARGE_FREE_DELIVERY : PACKING_CHARGE_STANDARD;
  }, [cartItems.length, subtotal]);

  const grandTotal = useMemo(() => {
    return subtotal - discountAmount + deliveryCharge + packingCharge;
  }, [subtotal, discountAmount, deliveryCharge, packingCharge]);

  // Option 1 Calculations (20% Advance / 80% Before Dispatch)
  const advancePayNow = useMemo(() => {
    return Math.round(grandTotal * 0.20);
  }, [grandTotal]);

  const advancePayLater = useMemo(() => {
    return grandTotal - advancePayNow;
  }, [grandTotal, advancePayNow]);

  // Option 2 Calculations (Full Prepaid - ₹200 discount)
  const prepaidPayNow = useMemo(() => {
    return Math.max(0, grandTotal - 200);
  }, [grandTotal]);

  // 3. Confirm & Lock Order Logic
  const handleConfirmOrder = async () => {
    // 0. Reset same-origin communication variable
    window.whatsappUrl = null;

    // 1. Open blank trusted tab instantly (synchronously within user gesture context)
    const whatsappWindow = window.open('', '_blank');
    if (whatsappWindow) {
      try {
        whatsappWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Confirming Your Order...</title>
              <meta charset="utf-8" />
              <meta name="viewport" content="width=device-width, initial-scale=1" />
              <style>
                body {
                  background-color: #09090b;
                  color: #fafafa;
                  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  height: 100vh;
                  margin: 0;
                  padding: 20px;
                  box-sizing: border-box;
                  text-align: center;
                }
                .spinner {
                  border: 4px solid rgba(255, 255, 255, 0.05);
                  width: 44px;
                  height: 44px;
                  border-radius: 50%;
                  border-left-color: #ff7a00;
                  animation: spin 1s linear infinite;
                  margin-bottom: 24px;
                }
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
                h2 {
                  font-size: 1.3rem;
                  font-weight: 600;
                  margin: 0 0 10px 0;
                  color: #ffffff;
                }
                p {
                  color: #a1a1aa;
                  margin: 0;
                  font-size: 0.95rem;
                  max-width: 360px;
                  line-height: 1.5;
                }
              </style>
            </head>
            <body>
              <div class="spinner"></div>
              <h2>Confirming Your Order</h2>
              <p>Please wait while we lock your order and open WhatsApp to finalize payment...</p>
              <script>
                // Polling script checking for the target WhatsApp URL set on the opener window.
                // Runs within same-origin boundaries to safely bypass browser popup blocking.
                var pollInterval = setInterval(function() {
                  try {
                    if (!window.opener || window.opener.closed) {
                      clearInterval(pollInterval);
                      return;
                    }
                    if (window.opener.whatsappUrl) {
                      var url = window.opener.whatsappUrl;
                      window.opener.whatsappUrl = null; // Clean up Immediately
                      clearInterval(pollInterval);
                      window.location.href = url;
                    }
                  } catch (e) {
                    clearInterval(pollInterval);
                  }
                }, 100);
              </script>
            </body>
          </html>
        `);
        whatsappWindow.document.close();
      } catch (err) {
        console.warn("Failed to write loading state to new tab:", err);
      }
    }

    if (!user) {
      if (whatsappWindow) whatsappWindow.close();
      alert("Please log in to confirm your order.");
      navigate('/login');
      return;
    }

    if (cartItems.length === 0) {
      if (whatsappWindow) whatsappWindow.close();
      alert("Your cart is empty.");
      navigate('/cart');
      return;
    }

    if (!selectedAddress) {
      if (whatsappWindow) whatsappWindow.close();
      alert("Delivery address is required. Redirecting to Step 1.");
      navigate('/checkout');
      return;
    }

    try {
      setLoading(true);

      // Create payload matching what backend expects
      const orderPayload = {
        items: cartItems.map(item => ({
          productId: item.productId,
          qty: item.qty,
          price: item.price
        })),
        address: {
          shopName: selectedAddress.shopName || "",
          contactPerson: selectedAddress.contactPerson || "",
          phone: selectedAddress.phone || "",
          street: selectedAddress.street || "",
          city: selectedAddress.city || "",
          state: selectedAddress.state || "",
          postalCode: selectedAddress.postalCode || "",
          country: selectedAddress.country || "India"
        },
        paymentMethod: paymentOption,
        couponCode: appliedCoupon ? appliedCoupon.code : "",
        coupon: appliedCoupon
      };

      // Call createOrder API (The backend now saves order and clears backend cart in background!)
      const response = await apiFetch('/api/orders', {
        method: 'POST',
        body: JSON.stringify(orderPayload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to place order. Please try again.');
      }

      // Order created successfully!
      const orderId = data._id;
      const orderNumber = data.orderNumber;
      const finalTotalAmount = data.totalAmount;

      // Clear Redux Cart and Session Storage locally
      dispatch(clearCart());
      sessionStorage.removeItem('selectedAddress');

      // Generate WhatsApp message dynamically from created order response
      const customerName = selectedAddress.shopName || selectedAddress.contactPerson || user.name;
      const paymentMethodName = paymentOption === 'advance' 
        ? '20% Advance' 
        : 'Full Prepaid';

      const whatsappMessage = `Hello Regard Traders,\n\nI have placed an order.\n\nOrder Number: ${orderNumber}\nCustomer: ${customerName}\nOrder Amount: ₹${finalTotalAmount.toLocaleString('en-IN')}\nPayment Method: ${paymentMethodName}\n\nPlease share payment details.\n\nThank you.`;
      
      const whatsappNumber = '917222994585';
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

      // Expose URL to same-origin child tab polling script
      window.whatsappUrl = whatsappUrl;

      const lastOrderDetails = {
        orderId,
        orderNumber,
        paymentMethod: paymentMethodName,
        grandTotal: finalTotalAmount,
        whatsappUrl,
        popupOpened: true
      };
      localStorage.setItem('lastOrderDetails', JSON.stringify(lastOrderDetails));

      // Navigate to success page
      navigate('/order-success', {
        state: lastOrderDetails
      });

    } catch (err) {
      // Close the pre-allocated window if order fails
      if (whatsappWindow && !whatsappWindow.closed) {
        whatsappWindow.close();
      }
      console.error(err);
      alert(err.message || 'Error occurred while creating order.');
    } finally {
      setLoading(false);
    }
  };

  // Render stepper component
  const renderStepper = () => (
    <div className="checkout-stepper-container" style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '30px' }}>
      <div className="checkout-stepper-step completed" onClick={() => navigate('/checkout')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div className="step-icon-badge" style={{ background: '#ff7a00', color: 'var(--color-text-primary)', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>✓</div>
        <span className="step-label-text" style={{ color: 'var(--color-text-primary)', fontWeight: '500' }}>Address & Order Review</span>
      </div>
      <div className="checkout-stepper-divider completed" style={{ height: '2px', background: 'rgba(255, 122, 0, 0.3)', alignSelf: 'center', width: '60px' }}></div>
      <div className="checkout-stepper-step active" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div className="step-icon-badge" style={{ background: '#ff7a00', color: 'var(--color-text-primary)', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>2</div>
        <span className="step-label-text" style={{ color: 'var(--color-text-primary)', fontWeight: '700' }}>Payment & Confirmation</span>
      </div>
    </div>
  );

  if (!user || cartItems.length === 0 || !selectedAddress) return null;

  return (
    <div className="checkout-page-wrapper" style={{ paddingBottom: '120px' }}>
      <div className="checkout-header">
        <h2>Payment & Confirmation</h2>
        <p>Step 2 of 2: Wholesale payment option and order confirmation</p>
      </div>

      {renderStepper()}

      <div className="checkout-layout">
        
        {/* Left Column: Select Payment Method */}
        <div className="checkout-left-column" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          <div className="checkout-card">
            <h3 className="checkout-card-title">Select Wholesale Payment Option</h3>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', marginBottom: '20px', marginTop: '-10px' }}>
              Select a payment terms method. Payment coordinates will be shared over WhatsApp.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              {/* Option 1: 20% Advance */}
              <div 
                className={`payment-option-card ${paymentOption === 'advance' ? 'selected' : ''}`}
                onClick={() => setPaymentOption('advance')}
                style={{
                  background: 'var(--color-surface-muted, rgba(24, 24, 27, 0.6))',
                  border: paymentOption === 'advance' ? '2px solid #ff7a00' : '1px solid var(--color-border-light, rgba(255, 255, 255, 0.08))',
                  borderRadius: '8px',
                  padding: '20px',
                  paddingTop: '30px',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  boxShadow: paymentOption === 'advance' ? '0 0 15px rgba(255, 122, 0, 0.15)' : 'none'
                }}
              >
                {/* Badge */}
                <div style={{
                  position: 'absolute',
                  top: '6px',
                  right: '8px',
                  background: '#ff7a00',
                  color: 'var(--color-text-primary)',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Recommended
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <input 
                    type="radio" 
                    name="paymentOption" 
                    checked={paymentOption === 'advance'}
                    onChange={() => setPaymentOption('advance')}
                    style={{ marginTop: '4px', accentColor: '#ff7a00', cursor: 'pointer' }}
                  />
                  <div style={{ width: '100%' }}>
                    <h4 style={{ color: 'var(--color-text-primary)', fontSize: '1.1rem', margin: '0 0 6px 0', fontWeight: '700' }}>
                      20% Advance + 80% Before Dispatch
                    </h4>
                    
                    {/* Calculations Display */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '20px', 
                      margin: '12px 0', 
                      padding: '10px 14px', 
                      background: 'var(--color-background, rgba(255,255,255,0.02))', 
                      borderRadius: '8px',
                      border: '1px solid var(--color-border-light, rgba(255,255,255,0.03))'
                    }}>
                      <div>
                        <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Pay Now (20%)</span>
                        <span style={{ fontSize: '1.2rem', fontWeight: '700', color: '#ff7a00' }}>
                          ₹{advancePayNow.toLocaleString('en-IN')}
                        </span>
                      </div>
                      <div style={{ borderLeft: '1px solid var(--color-border-light, rgba(255,255,255,0.08))', paddingLeft: '20px' }}>
                        <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Pay Before Dispatch (80%)</span>
                        <span style={{ fontSize: '1.2rem', fontWeight: '700', color: 'var(--color-text-primary)' }}>
                          ₹{advancePayLater.toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>

                    <div style={{ marginTop: '10px' }}>
                      <span style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-primary)', marginBottom: '4px' }}>Benefits:</span>
                      <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--color-text-secondary)', fontSize: '0.85rem', lineHeight: '1.5' }}>
                        <li>Inventory Reserved Immediately</li>
                        <li>Faster Order Processing</li>
                        <li>Most Popular Option</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Option 2: Full Prepaid */}
              <div 
                className={`payment-option-card ${paymentOption === 'full_prepaid' ? 'selected' : ''}`}
                onClick={() => setPaymentOption('full_prepaid')}
                style={{
                  background: 'var(--color-surface-muted, rgba(24, 24, 27, 0.6))',
                  border: paymentOption === 'full_prepaid' ? '2px solid #ff7a00' : '1px solid var(--color-border-light, rgba(255, 255, 255, 0.08))',
                  borderRadius: '8px',
                  padding: '20px',
                  paddingTop: '30px',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  boxShadow: paymentOption === 'full_prepaid' ? '0 0 15px rgba(255, 122, 0, 0.15)' : 'none'
                }}
              >
                {/* Badge */}
                <div style={{
                  position: 'absolute',
                  top: '6px',
                  right: '8px',
                  background: '#10b981',
                  color: 'var(--color-text-primary)',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Save ₹200
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <input 
                    type="radio" 
                    name="paymentOption" 
                    checked={paymentOption === 'full_prepaid'}
                    onChange={() => setPaymentOption('full_prepaid')}
                    style={{ marginTop: '4px', accentColor: '#ff7a00', cursor: 'pointer' }}
                  />
                  <div style={{ width: '100%' }}>
                    <h4 style={{ color: 'var(--color-text-primary)', fontSize: '1.1rem', margin: '0 0 6px 0', fontWeight: '700' }}>
                      Full Prepaid
                    </h4>

                    {/* Calculations Display */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '20px', 
                      margin: '12px 0', 
                      padding: '10px 14px', 
                      background: 'var(--color-background, rgba(255,255,255,0.02))', 
                      borderRadius: '8px',
                      border: '1px solid var(--color-border-light, rgba(255,255,255,0.03))'
                    }}>
                      <div>
                        <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Pay Now (Discounted)</span>
                        <span style={{ fontSize: '1.2rem', fontWeight: '700', color: '#ff7a00' }}>
                          ₹{prepaidPayNow.toLocaleString('en-IN')}
                        </span>
                      </div>
                      <div style={{ borderLeft: '1px solid var(--color-border-light, rgba(255,255,255,0.08))', paddingLeft: '20px', display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: '600' }}>
                          ₹200 discount applied
                        </span>
                      </div>
                    </div>

                    <div style={{ marginTop: '10px' }}>
                      <span style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-primary)', marginBottom: '4px' }}>Benefits:</span>
                      <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--color-text-secondary)', fontSize: '0.85rem', lineHeight: '1.5' }}>
                        <li>Priority Dispatch</li>
                        <li>Additional Discount</li>
                        <li>No Pending Payment</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Right Column: Explanation & Info Cards */}
        <div className="checkout-right-column" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Order Process Card */}
          <div className="checkout-card">
            <h3 className="checkout-card-title">Order Fulfillment Process</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>
              {[
                { step: '1', title: 'Confirm Order', desc: 'Lock the products in your cart by placing the order.' },
                { step: '2', title: 'WhatsApp Opens', desc: 'Our configured wholesale WhatsApp chat opens automatically.' },
                { step: '3', title: 'Order ID Generated', desc: 'The system logs your order and generates a unique ID.' },
                { step: '4', title: 'Team Contacts You', desc: 'Our wholesale coordination team reaches out for verification.' },
                { step: '5', title: 'Payment Collected', desc: 'Payment details are shared and collected securely.' },
                { step: '6', title: 'Inventory Locked', desc: 'Goods are dispatched once payment is confirmed.' }
              ].map((item, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'rgba(255, 122, 0, 0.1)',
                    border: '1px solid #ff7a00',
                    color: '#ff7a00',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    fontSize: '0.8rem',
                    flexShrink: 0
                  }}>
                    {item.step}
                  </div>
                  <div>
                    <span style={{ display: 'block', color: 'var(--color-text-primary)', fontWeight: '600', fontSize: '0.9rem' }}>{item.title}</span>
                    <span style={{ display: 'block', color: 'var(--color-text-secondary)', fontSize: '0.8rem', marginTop: '2px' }}>{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Important Information Card */}
          <div className="checkout-card" style={{ borderLeft: '4px solid #ff7a00' }}>
            <h3 className="checkout-card-title">Important Information</h3>
            <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--color-text-secondary)', fontSize: '0.85rem', display: 'flex', flexDirection: 'column', gap: '8px', lineHeight: '1.4' }}>
              <li>Inventory is reserved for <strong>15 minutes</strong>. Please complete the coordination to avoid cancellation.</li>
              <li>Payment details are shared **only** through official Regard Traders channels.</li>
              <li>Orders are confirmed and packed only after payment verification.</li>
            </ul>
          </div>

        </div>

      </div>

      {/* Desktop Sticky Footer */}
      <div className="payment-footer-desktop">
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Selected Terms
          </span>
          <span style={{ color: 'var(--color-text-primary)', fontWeight: '700', fontSize: '1.1rem', marginTop: '2px' }}>
            {paymentOption === 'advance' ? '20% Advance / 80% Before Dispatch' : 'Full Prepaid'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
          <div style={{ textAlign: 'right' }}>
            <span style={{ display: 'block', color: 'var(--color-text-secondary)', fontSize: '0.8rem' }}>Pay Now</span>
            <span style={{ color: '#ff7a00', fontSize: '1.6rem', fontWeight: '800' }}>
              ₹{(paymentOption === 'advance' ? advancePayNow : prepaidPayNow).toLocaleString('en-IN')}
            </span>
          </div>
          <button 
            onClick={handleConfirmOrder} 
            disabled={loading}
            className="btn-checkout-primary"
            style={{ minWidth: '240px', padding: '14px 30px' }}
          >
            {loading ? 'Processing...' : 'Confirm & Lock Order'}
          </button>
        </div>
      </div>

      {/* Mobile Sticky Footer */}
      <div className="checkout-footer-mobile">
        <div className="checkout-footer-mobile-info">
          <span className="mobile-grand-total-label">Pay Now</span>
          <span className="mobile-grand-total-val">
            ₹{(paymentOption === 'advance' ? advancePayNow : prepaidPayNow).toLocaleString('en-IN')}
          </span>
        </div>
        <button 
          onClick={handleConfirmOrder} 
          disabled={loading}
          className="btn-checkout-primary-mobile"
        >
          {loading ? 'Processing...' : 'Confirm & Lock'}
        </button>
      </div>

    </div>
  );
};

export default Payment;
